// Authentication service for API calls
// In production, use environment variable or fallback to relative /api path
const API_URL = process.env.REACT_APP_API_URL || '/api';

class AuthService {
    // Get stored token
    getToken() {
        return localStorage.getItem('token');
    }

    // Get stored user
    getUser() {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    }

    // Check if user is authenticated
    isAuthenticated() {
        return !!this.getToken();
    }

    // Check if user is admin
    isAdmin() {
        const user = this.getUser();
        return user?.role === 'Admin' || user?.role === 'SuperAdmin';
    }

    // Check if user is super admin
    isSuperAdmin() {
        const user = this.getUser();
        return user?.role === 'SuperAdmin';
    }

    // Get user role
    getUserRole() {
        const user = this.getUser();
        return user?.role || 'Basic';
    }

    // Login
    async login(email, password) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        try {
            const response = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            console.log('🔍 Response status:', response.status);
            console.log('🔍 Response ok:', response.ok);

            if (!response.ok) {
                let errorMessage = 'Login failed';
                try {
                    const errorData = await response.json();
                    console.log('🔍 Error data:', errorData);
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } catch {
                    try {
                        const errorText = await response.text();
                        errorMessage = errorText || errorMessage;
                    } catch {
                    }
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            console.log('🔍 Success data:', data);
            
            if (!data.token) {
                if (data.error) {
                    throw new Error(data.error);
                }
                throw new Error('Invalid server response: missing token');
            }
            
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data));
            return data;
        } catch (error) {
            console.error('🔍 Login error:', error);
            if (error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
                throw new Error('Cannot connect to server. Please try again later.');
            }
            throw error;
        }
    }

    // Register
    async register(email, password, firstName, lastName) {
        const response = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, firstName, lastName })
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(error || 'Registration failed');
        }

        const data = await response.json();
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data));
        return data;
    }

    // Logout
    logout() {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
    }

    // Fetch with authentication
    async fetchWithAuth(url, options = {}) {
        const token = this.getToken();
        const headers = {
            ...options.headers,
            'Authorization': token ? `Bearer ${token}` : ''
        };

        const response = await fetch(url, { ...options, headers });

        // Handle 401 Unauthorized
        if (response.status === 401) {
            this.logout();
            throw new Error('Session expired. Please login again.');
        }

        return response;
    }
}

const authServiceInstance = new AuthService();
export default authServiceInstance;
